import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ChatCircle, MapPin, PencilSimple, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const Profile = () => {
  const { userId } = useParams();
  const { user: me, setUser } = useAuth();
  const [u, setU] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ headline: "", bio: "", location: "", skills: "" });
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/users/${userId}`).then((r) => {
      setU(r.data);
      setForm({
        headline: r.data.headline || "",
        bio: r.data.bio || "",
        location: r.data.location || "",
        skills: (r.data.skills || []).join(", "),
      });
    });
  }, [userId]);

  const isMe = me?.user_id === userId;

  const save = async () => {
    const payload = {
      headline: form.headline,
      bio: form.bio,
      location: form.location,
      skills: form.skills.split(",").map(s => s.trim()).filter(Boolean),
    };
    const r = await api.put("/users/me", payload);
    setU({ ...u, ...r.data });
    setUser(r.data);
    setEditing(false);
    toast.success("PROFILE UPDATED");
  };

  const startChat = async () => {
    const r = await api.post("/conversations", { other_user_id: userId });
    navigate(`/app/messages/${r.data.id}`);
  };

  if (!u) return <div className="p-8 text-sm text-ink-3">LOADING…</div>;

  return (
    <div className="min-h-screen">
      <div className="border-b border-line">
        <div className="grid grid-cols-12">
          <div className="col-span-12 lg:col-span-8 border-r border-line p-8">
            <div className="flex gap-6">
              <img src={u.picture || `https://api.dicebear.com/9.x/notionists/svg?seed=${u.user_id}`} className="w-28 h-28 border border-line object-cover" alt="" />
              <div className="flex-1 min-w-0">
                <div className="label-tag text-ink-3">// PROFILE — {u.user_id.slice(0, 8)}</div>
                <h1 className="font-heading font-black text-4xl sm:text-5xl mt-1">{u.name}</h1>
                <div className="text-sm text-ink-2 mt-2">{u.headline || "Member"}</div>
                <div className="text-xs text-ink-3 mt-2 flex items-center gap-3">
                  <span className="flex items-center gap-1"><MapPin size={12} />{u.location || "—"}</span>
                  <span>·</span>
                  <span>{u.connections_count || 0} CONNECTIONS</span>
                </div>
                <div className="mt-5 flex gap-2">
                  {isMe ? (
                    <button onClick={() => setEditing(true)} className="btn-secondary flex items-center gap-2" data-testid="edit-profile-btn">
                      <PencilSimple size={14} /> EDIT PROFILE
                    </button>
                  ) : (
                    <button onClick={startChat} className="btn-primary flex items-center gap-2" data-testid="profile-chat-btn">
                      <ChatCircle size={14} /> MESSAGE
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
          <aside className="col-span-12 lg:col-span-4 p-8 bg-paper-tint">
            <div className="label-tag text-ink-3">// SKILLS</div>
            <div className="mt-4 flex flex-wrap gap-2">
              {(u.skills || []).length === 0 && <div className="text-xs text-ink-3">— No skills listed —</div>}
              {(u.skills || []).map((s) => (
                <span key={s} className="border border-ink px-3 py-1 text-xs font-mono">{s}</span>
              ))}
            </div>
          </aside>
        </div>
      </div>
      <div className="p-8 max-w-3xl">
        <div className="label-tag text-ink-3">// BIO</div>
        <p className="mt-3 text-sm leading-relaxed whitespace-pre-wrap">{u.bio || "— No bio written yet —"}</p>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-ink/40 flex items-center justify-center p-4 z-50" onClick={() => setEditing(false)}>
          <div className="brut-card max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-line p-4 flex items-center justify-between">
              <h3 className="font-heading font-bold">EDIT PROFILE</h3>
              <button onClick={() => setEditing(false)}><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <div className="label-tag mb-2">HEADLINE</div>
                <input value={form.headline} onChange={(e) => setForm({...form, headline: e.target.value})} className="w-full border border-line p-3 text-sm" data-testid="edit-headline" />
              </div>
              <div>
                <div className="label-tag mb-2">LOCATION</div>
                <input value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} className="w-full border border-line p-3 text-sm" data-testid="edit-location" />
              </div>
              <div>
                <div className="label-tag mb-2">SKILLS (comma-separated)</div>
                <input value={form.skills} onChange={(e) => setForm({...form, skills: e.target.value})} className="w-full border border-line p-3 text-sm" data-testid="edit-skills" />
              </div>
              <div>
                <div className="label-tag mb-2">BIO</div>
                <textarea value={form.bio} onChange={(e) => setForm({...form, bio: e.target.value})} rows={5} className="w-full border border-line p-3 text-sm resize-none" data-testid="edit-bio" />
              </div>
              <button onClick={save} className="btn-primary w-full" data-testid="save-profile">SAVE</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
